import { setZodIssueReporter } from '@whiz-cart/node-shared/zod/issueReporter';
import { useCallback, useEffect } from 'react';

type UseZodIssueReportingOptions = {
    stackTraceLimit: number;
    enableCleanStackTrace: boolean;
};

const defaultUseZodIssueReportingOptions = {
    stackTraceLimit: 32,
    enableCleanStackTrace: true,
} satisfies UseZodIssueReportingOptions;

export function useZodIssueReporting(options: Partial<UseZodIssueReportingOptions> = {}) {
    const { stackTraceLimit, enableCleanStackTrace } = { ...defaultUseZodIssueReportingOptions, ...options };

    const makeError = useCallback(
        (message?: string) => {
            const previousLimit = Error.stackTraceLimit;
            Error.stackTraceLimit = stackTraceLimit;
            const error = new Error(message);
            if (error.stack && enableCleanStackTrace) {
                const lines = error.stack.split('\n');
                error.stack = lines.slice(3).join('\n');
            }

            Error.stackTraceLimit = previousLimit;
            return error;
        },
        [stackTraceLimit, enableCleanStackTrace],
    );

    useEffect(() => {
        console.debug(`Registering Zod issue reporter`);
        setZodIssueReporter(({ issues, value, ...params }) => {
            const message: unknown[] = [`Zod ${params.source} caught ${issues.length} ${issues.length > 1 ? 'issues' : 'issue'}`];
            if (params.source === 'forgivingSchema') {
                message.push('at path:', params.path, '\n');
            }
            const error = makeError('stack trace:');
            console.error(...message, { issues }, { value }, '\n', error);
        });
    }, [makeError]);
}
